import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  CalendarDays,
  ChevronRight,
  Mail,
  MessageSquare,
  Phone,
  Repeat,
  Search,
  UserPlus,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  FacilityPatientTimelineSheet,
  type FacilityPatient,
} from "@/components/account/facility/facility-patient-timeline-sheet";
import {
  FacilityStateCard,
  facilityErrorKind,
} from "@/components/account/facility/facility-state-card";
import { PageHeader } from "@/components/layout/page-header";
import { FacilityPatientMessagingSheet } from "@/components/messaging/facility-patient-messaging-sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/stat-card";

interface PatientsResponse {
  patients: FacilityPatient[];
  kpis: {
    totalPatients: number;
    newThisMonth: number;
    returningPercent: number;
    avgVisits: number;
  };
}

// Client-side page size. The API (and legacy before it) caps the payload at
// 200 patients, so paging happens over the already-fetched, filtered list.
const PAGE_SIZE = 50;

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/patients")({
  component: FacilityPatientsPage,
});

function FacilityPatientsPage() {
  const { facilityId } = Route.useParams();
  const { i18n, t } = useLingui();
  const locale = i18n.locale;

  const [query, setQuery] = useState("");
  const [rawPage, setRawPage] = useState(1);
  // Selected patient drives the timeline slide-in sheet (legacy
  // facility-patients-client.tsx:267-380 side panel).
  const [selectedPatient, setSelectedPatient] = useState<FacilityPatient | null>(null);
  // Messaging surface, restored from legacy (facility-patients-client.tsx:424-440
  // swapped the whole page for <MessagingClient>; here it opens in a sheet).
  const [messagingPatient, setMessagingPatient] = useState<FacilityPatient | null>(null);

  const patientsQ = useQuery({
    queryKey: ["api", `/api/account/facility/${facilityId}/patients`] as const,
    queryFn: ({ signal }) =>
      apiFetch.get<PatientsResponse>(`/api/account/facility/${facilityId}/patients`, { signal }),
    retry: false,
  });
  const data = patientsQ.data ?? null;

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    if (!q) return data.patients;
    return data.patients.filter((p) => {
      const haystack = [p.firstName, p.lastName, p.phone ?? "", p.email ?? ""]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [data, query]);

  // Clamp instead of resetting via effect — a shrinking filter result never
  // leaves the user stranded on an empty page.
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const page = Math.min(rawPage, totalPages);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  // Member expressions on purpose: produces the shared "{0} of {1} • {2}"
  // msgid already used by the activity-log pagination.
  const pageInfo = { page, totalPages, total: filtered.length };

  if (patientsQ.isLoading) return <PatientsSkeleton />;
  if (patientsQ.isError || !data) {
    return (
      <FacilityStateCard
        kind={patientsQ.isError ? facilityErrorKind(patientsQ.error) : "load-failed"}
        facilityId={facilityId}
        onRetry={() => void patientsQ.refetch()}
        retrying={patientsQ.isFetching}
      />
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={<Trans>Patients</Trans>}
        description={<Trans>People who've booked at this facility.</Trans>}
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label={t`Total`} value={data.kpis.totalPatients} icon={Users} />
        <StatCard
          label={t`New this month`}
          value={data.kpis.newThisMonth}
          icon={UserPlus}
          tone="success"
        />
        <StatCard
          label={t`Returning`}
          value={data.kpis.returningPercent / 100}
          format={{ style: "percent", maximumFractionDigits: 0 }}
          icon={Repeat}
          tone="accent"
        />
        <StatCard
          label={t`Avg visits`}
          value={data.kpis.avgVisits}
          format={{ maximumFractionDigits: 1 }}
          icon={CalendarDays}
          tone="info"
        />
      </div>

      {/* Search */}
      <div className="border-border bg-card focus-within:border-primary/50 flex items-center gap-2 rounded-xl border px-3">
        <Search className="text-muted-foreground size-4 flex-shrink-0" aria-hidden />
        <input
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setRawPage(1);
          }}
          placeholder={t`Search by name, phone, or email…`}
          className="text-foreground placeholder:text-muted-foreground/60 min-h-[44px] w-full bg-transparent text-sm outline-none"
        />
      </div>

      {/* Patient list */}
      {filtered.length === 0 ? (
        <div className="border-border bg-card flex flex-col items-center gap-2 rounded-xl border py-12 text-center">
          <Users className="text-muted-foreground/40 size-8" aria-hidden />
          <p className="text-muted-foreground text-sm">
            {query ? (
              <Trans>No patients match your search.</Trans>
            ) : (
              <Trans>No patients yet.</Trans>
            )}
          </p>
        </div>
      ) : (
        <ul className="border-border bg-card divide-border divide-y overflow-hidden rounded-xl border">
          {pageItems.map((patient) => {
            const lastVisit = new Date(patient.lastVisit).toLocaleDateString(locale, {
              day: "2-digit",
              month: "short",
              year: "numeric",
            });
            // Row = timeline button + "Send message" action, as SIBLINGS.
            // Legacy nested the message button inside the row button
            // (facility-patients-client.tsx:529-540) — invalid HTML, and the
            // inner click has to fight the outer handler.
            return (
              <li
                key={patient.id}
                className="hover:bg-muted/40 flex items-center transition-colors"
              >
                <button
                  type="button"
                  onClick={() => setSelectedPatient(patient)}
                  className="flex min-h-[44px] min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left"
                >
                  {patient.photoUrl ? (
                    <img
                      src={patient.photoUrl}
                      alt={`${patient.firstName} ${patient.lastName}`}
                      className="size-10 flex-shrink-0 rounded-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="bg-primary-soft text-primary flex size-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                      {(patient.firstName[0] ?? "?") + (patient.lastName[0] ?? "")}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground truncate text-sm font-medium">
                      {patient.firstName} {patient.lastName}
                    </p>
                    <p className="text-muted-foreground flex items-center gap-2 truncate text-xs">
                      {patient.phone && (
                        <span className="inline-flex items-center gap-0.5">
                          <Phone className="size-3" aria-hidden />
                          {patient.phone}
                        </span>
                      )}
                      {patient.email && (
                        <span className="inline-flex items-center gap-0.5 truncate">
                          <Mail className="size-3" aria-hidden />
                          {patient.email}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-foreground text-xs font-medium">
                      {patient.totalVisits}{" "}
                      <span className="text-muted-foreground">
                        <Trans>visits</Trans>
                      </span>
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-xs">
                      <Trans>last {lastVisit}</Trans>
                    </p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setMessagingPatient(patient)}
                  aria-label={t`Send message to ${patient.firstName} ${patient.lastName}`}
                  title={t`Send message`}
                  className="text-muted-foreground hover:text-primary flex size-11 shrink-0 items-center justify-center transition-colors"
                >
                  <MessageSquare className="size-4" aria-hidden />
                </button>
                <ChevronRight className="text-muted-foreground mr-3 size-4 shrink-0" aria-hidden />
              </li>
            );
          })}
        </ul>
      )}

      {/* Pagination — same pattern as activity-log-client.tsx */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2">
          <p className="text-muted-foreground text-xs tabular-nums">
            <Trans>
              Page {pageInfo.page} of {pageInfo.totalPages} • {pageInfo.total} total
            </Trans>
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="touch"
              disabled={page <= 1}
              onClick={() => setRawPage(Math.max(1, page - 1))}
            >
              <Trans>Previous</Trans>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="touch"
              disabled={page >= totalPages}
              onClick={() => setRawPage(page + 1)}
            >
              <Trans>Next</Trans>
            </Button>
          </div>
        </div>
      )}

      {/* Per-patient activity timeline (appointments + reviews + messages) */}
      <FacilityPatientTimelineSheet
        facilityId={facilityId}
        patient={selectedPatient}
        onClose={() => setSelectedPatient(null)}
      />

      {/* Conversation with one patient (thread list + composer) */}
      <FacilityPatientMessagingSheet
        facilityId={facilityId}
        patient={messagingPatient}
        onClose={() => setMessagingPatient(null)}
      />

      <AccountSecurityBanner />
    </div>
  );
}

function PatientsSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="h-10 w-48" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
      <Skeleton className="h-12 rounded-xl" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  );
}
